import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ClipboardDocumentListIcon, ClockIcon, UserIcon,
  CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { testApi } from '../services/api';
// Importar bancos de preguntas reales desde archivos de datos
import mbtiQuestions from '../data/mbtiQuestions';
import viaQuestions, { viaLikertOptions } from '../data/viaQuestions';
import enneagramQuestions from '../data/enneagramQuestions';

function Tests() {
  const navigate = useNavigate();
  // Paso actual: 0=selección de test, 1=realizando test
  const [currentStep, setCurrentStep] = useState(0);
  // Tests seleccionados por el usuario
  const [selectedTests, setSelectedTests] = useState([]);
  // Índice de la pregunta actual que se muestra en pantalla
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // Respuestas almacenadas para cada tipo de test
  const [mbtiAnswers, setMbtiAnswers] = useState({});
  const [viaAnswers, setViaAnswers] = useState({});
  const [enneagramAnswers, setEnneagramAnswers] = useState({});
  // Estado de envío del formulario
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Definición de los tests disponibles con cantidad real de preguntas
  const tests = [
    { id: 'mbti', name: '16 Personalidades (MBTI)', description: 'Descubre tu tipo de personalidad basado en las dimensiones de Myers-Briggs', duration: '15 minutos', questions: mbtiQuestions.length, icon: UserIcon },
    { id: 'via', name: 'Fortalezas de Carácter (VIA)', description: 'Identifica tus fortalezas de carácter principales', duration: '20 minutos', questions: viaQuestions.length, icon: ClipboardDocumentListIcon },
    { id: 'enneagram', name: 'Eneagrama', description: 'Explora tus motivaciones profundas y patrones de comportamiento', duration: '10 minutos', questions: enneagramQuestions.length, icon: ClockIcon },
  ];

  // Alternar selección de un test
  const handleTestSelection = (testId) => {
    if (selectedTests.includes(testId)) {
      setSelectedTests(selectedTests.filter(id => id !== testId));
    } else {
      setSelectedTests([...selectedTests, testId]);
    }
  };

  // Obtener el banco de preguntas del test activo
  const getActiveQuestions = () => {
    switch (selectedTests[0]) {
      case 'mbti': return mbtiQuestions;
      case 'via': return viaQuestions;
      case 'enneagram': return enneagramQuestions;
      default: return [];
    }
  };

  // Obtener las respuestas actuales del test activo
  const getActiveAnswers = () => {
    switch (selectedTests[0]) {
      case 'mbti': return mbtiAnswers;
      case 'via': return viaAnswers;
      case 'enneagram': return enneagramAnswers;
      default: return {};
    }
  };

  // Registrar respuesta MBTI y avanzar automáticamente a la siguiente pregunta
  const handleMBTIAnswer = (questionId, answer) => {
    const newAnswers = { ...mbtiAnswers, [questionId]: answer };
    setMbtiAnswers(newAnswers);
    // Auto-avanzar a la siguiente pregunta después de 300ms
    if (currentQuestionIndex < mbtiQuestions.length - 1) {
      setTimeout(() => setCurrentQuestionIndex(prev => prev + 1), 300);
    }
  };

  // Registrar respuesta VIA y avanzar automáticamente
  const handleVIAAnswer = (questionId, answer) => {
    const newAnswers = { ...viaAnswers, [questionId]: answer };
    setViaAnswers(newAnswers);
    if (currentQuestionIndex < viaQuestions.length - 1) {
      setTimeout(() => setCurrentQuestionIndex(prev => prev + 1), 300);
    }
  };

  // Registrar respuesta Eneagrama y avanzar automáticamente
  const handleEnneagramAnswer = (questionId, answer) => {
    const newAnswers = { ...enneagramAnswers, [questionId]: answer };
    setEnneagramAnswers(newAnswers);
    if (currentQuestionIndex < enneagramQuestions.length - 1) {
      setTimeout(() => setCurrentQuestionIndex(prev => prev + 1), 300);
    }
  };

  // Calcular resultados MBTI reales basados en las respuestas del usuario
  const calculateMBTIResults = () => {
    // Contadores para cada polo de las 4 dimensiones
    const scores = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };
    // Sumar respuestas por dimensión
    Object.values(mbtiAnswers).forEach(val => { if (scores[val] !== undefined) scores[val]++; });
    // Determinar el tipo de personalidad según el polo con mayor puntuación
    const type = (scores.E >= scores.I ? 'E' : 'I') + (scores.S >= scores.N ? 'S' : 'N') +
                 (scores.T >= scores.F ? 'T' : 'F') + (scores.J >= scores.P ? 'J' : 'P');
    // Calcular porcentaje de definición (qué tan clara es la preferencia)
    const total = Object.keys(mbtiAnswers).length;
    const pctScore = total > 0 ? Math.round((Math.max(scores.E,scores.I) + Math.max(scores.S,scores.N) + Math.max(scores.T,scores.F) + Math.max(scores.J,scores.P)) / total * 100) : 0;
    return {
      personality_type: type, scores,
      description: `Tipo ${type} - Basado en ${total} respuestas`,
      strengths: [scores.E > scores.I ? 'Sociabilidad' : 'Reflexión', scores.N > scores.S ? 'Intuición' : 'Pragmatismo', scores.F > scores.T ? 'Empatía' : 'Análisis'],
      weaknesses: [scores.I > scores.E ? 'Puede ser reservado' : 'Puede agotarse socialmente', scores.P > scores.J ? 'Puede ser indeciso' : 'Puede ser rígido'],
      percentage_score: Math.min(pctScore, 95)
    };
  };

  // Calcular resultados VIA reales basados en las respuestas del usuario
  const calculateVIAResults = () => {
    // Sumar puntuaciones por fortaleza
    const strengthScores = {};
    viaQuestions.forEach(q => {
      if (!strengthScores[q.strength]) strengthScores[q.strength] = { total: 0, count: 0, virtue: q.virtue };
      if (viaAnswers[q.id] !== undefined) { strengthScores[q.strength].total += viaAnswers[q.id]; strengthScores[q.strength].count++; }
    });
    // Ordenar fortalezas por promedio descendente
    const sorted = Object.entries(strengthScores)
      .map(([name, data]) => ({ name, avg: data.count > 0 ? data.total / data.count : 0, virtue: data.virtue }))
      .sort((a, b) => b.avg - a.avg);
    const topNames = sorted.slice(0, 5).map(s => s.name);
    const totalAnswered = Object.keys(viaAnswers).length;
    const totalScore = Object.values(viaAnswers).reduce((a, b) => a + b, 0);
    return {
      top_strengths: topNames, signature_strengths: topNames.slice(0, 3), all_strengths: sorted.map(s => s.name),
      description: `Top fortalezas: ${topNames.slice(0, 3).join(', ')}`,
      percentage_score: totalAnswered > 0 ? Math.round((totalScore / (totalAnswered * 5)) * 100) : 0
    };
  };

  // Calcular resultados Eneagrama reales basados en las respuestas del usuario
  const calculateEnneagramResults = () => {
    // Contar cuántas veces se seleccionó cada tipo
    const typeScores = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
    enneagramQuestions.forEach(q => {
      const answer = enneagramAnswers[q.id];
      if (answer === 'A') typeScores[q.typeA]++;
      else if (answer === 'B') typeScores[q.typeB]++;
    });
    // Encontrar tipo dominante y alas
    const sorted = Object.entries(typeScores).sort((a, b) => b[1] - a[1]);
    const primaryType = sorted[0][0];
    const totalAnswered = Object.keys(enneagramAnswers).length;
    return {
      primary_type: primaryType,
      wing_types: [`${primaryType}w${sorted[1][0]}`, `${primaryType}w${sorted[2][0]}`],
      description: `Tipo ${primaryType} - ${getEnneagramTypeName(primaryType)}`,
      percentage_score: totalAnswered > 0 ? Math.round((sorted[0][1] / totalAnswered) * 100) : 0
    };
  };

  // Obtener nombre descriptivo del tipo de Eneagrama
  const getEnneagramTypeName = (type) => {
    const names = { '1': 'El Reformador', '2': 'El Ayudador', '3': 'El Triunfador', '4': 'El Individualista', '5': 'El Investigador', '6': 'El Leal', '7': 'El Entusiasta', '8': 'El Desafiador', '9': 'El Pacificador' };
    return names[type] || '';
  };

  // Enviar resultados al backend
  const submitTestResults = async () => {
    // Activar spinner e inhabilitar botón de finalizar
    setIsSubmitting(true);
    try {
      // Obtener el participante activo guardado en la sesión (localStorage)
      const activeParticipantStr = localStorage.getItem('activeParticipant');
      // Validar si la sesión sigue activa antes de proceder a guardar
      if (!activeParticipantStr) {
        // Notificar al usuario e interrumpir
        toast.error('La sesión ha expirado o no es válida. Por favor inicia sesión.');
        navigate('/login');
        return;
      }
      
      // Parsear la cadena JSON a un objeto JavaScript de participante
      const activeParticipant = JSON.parse(activeParticipantStr);
      const testType = selectedTests[0];
      // Calcular resultados reales según el tipo de test
      let results;
      switch (testType) {
        case 'mbti': results = calculateMBTIResults(); break;
        case 'via': results = calculateVIAResults(); break;
        case 'enneagram': results = calculateEnneagramResults(); break;
        default: results = {};
      }
      // Crear sesión de test en el backend utilizando el ID real del participante activo
      const sessionResponse = await testApi.createSession({ 
        participant_id: activeParticipant.id, // <-- ID dinámico y consistente
        test_type: testType, 
        results 
      });
      // Calcular y guardar puntuaciones de madurez basadas en los resultados reales
      const maturityScores = calculateMaturityScores(results, testType);
      await testApi.storeMaturityScores({ session_id: sessionResponse.data.session_id, pillar_scores: maturityScores });
      toast.success('¡Resultados guardados exitosamente!');
      navigate('/results');
    } catch (error) {
      toast.error('Error al guardar los resultados');
      console.error('Test submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calcular puntuaciones de madurez basadas en resultados reales del test
  const calculateMaturityScores = (results, testType) => {

    // Para MBTI: cada pilar se calcula con las dimensiones psicológicas más relevantes
    if (testType === 'mbti' && results.scores) {
      const s = results.scores; // Puntuaciones E, I, S, N, T, F, J, P

      // Función auxiliar: calcula qué tan fuerte es un polo relativo al total del eje
      // Ejemplo: poleStrength('F', 'T') = F/(F+T) * 100
      const poleStrength = (a, b) => {
        const total = (s[a] || 0) + (s[b] || 0);
        return total > 0 ? Math.round(((s[a] || 0) / total) * 100) : 50;
      };

      // --- Inteligencia Emocional ---
      // Depende de F (sentimiento/empatía) y I (reflexión/introspección)
      // Alto F = más empático. Alto I = más reflexivo.
      const fPct = poleStrength('F', 'T'); // 100 = completamente F
      const iPct = poleStrength('I', 'E'); // 100 = completamente I
      // Promedio ponderado: 60% peso en F, 40% en I
      const ieScore = Math.round(fPct * 0.6 + iPct * 0.4);

      // --- Gestión del Tiempo ---
      // Depende de J (orden/planificación) y S (pragmatismo/detalle)
      // Alto J = estructurado. Alto S = orientado a hechos concretos.
      const jPct = poleStrength('J', 'P'); // 100 = completamente J
      const sPct = poleStrength('S', 'N'); // 100 = completamente S
      // Promedio ponderado: 70% peso en J, 30% en S
      const gtScore = Math.round(jPct * 0.7 + sPct * 0.3);

      // --- Liderazgo y Toma de Decisiones ---
      // Depende de E (extroversión/iniciativa) y T (lógica/objetividad)
      // Alto E = proactivo, toma iniciativa. Alto T = decide con criterio racional.
      const ePct = poleStrength('E', 'I'); // 100 = completamente E
      const tPct = poleStrength('T', 'F'); // 100 = completamente T
      // Promedio ponderado: 50% E, 50% T
      const ldScore = Math.round(ePct * 0.5 + tPct * 0.5);

      // --- Adaptabilidad al Cambio ---
      // Depende de N (intuición/apertura) y P (flexibilidad/espontaneidad)
      // Alto N = visión amplia, tolerancia a la ambigüedad. Alto P = flexible.
      const nPct = poleStrength('N', 'S'); // 100 = completamente N
      const pPct = poleStrength('P', 'J'); // 100 = completamente P
      // Promedio ponderado: 60% N, 40% P
      const adScore = Math.round(nPct * 0.6 + pPct * 0.4);

      return [
        {
          pillar_name: 'Inteligencia Emocional',
          percentage_score: Math.min(ieScore, 100),
          maturity_level: getMaturityLevel(ieScore),
          evidence_notes: `Basado en MBTI — F:${s.F||0} T:${s.T||0} I:${s.I||0} E:${s.E||0}`
        },
        {
          pillar_name: 'Gestión del Tiempo',
          percentage_score: Math.min(gtScore, 100),
          maturity_level: getMaturityLevel(gtScore),
          evidence_notes: `Basado en MBTI — J:${s.J||0} P:${s.P||0} S:${s.S||0} N:${s.N||0}`
        },
        {
          pillar_name: 'Liderazgo y Toma de Decisiones',
          percentage_score: Math.min(ldScore, 100),
          maturity_level: getMaturityLevel(ldScore),
          evidence_notes: `Basado en MBTI — E:${s.E||0} I:${s.I||0} T:${s.T||0} F:${s.F||0}`
        },
        {
          pillar_name: 'Adaptabilidad al Cambio',
          percentage_score: Math.min(adScore, 100),
          maturity_level: getMaturityLevel(adScore),
          evidence_notes: `Basado en MBTI — N:${s.N||0} S:${s.S||0} P:${s.P||0} J:${s.J||0}`
        },
      ];
    }

    // Para VIA y Eneagrama: usar el puntaje base con variaciones leves por pilar
    const baseScore = results.percentage_score || 50;
    return [
      { pillar_name: 'Inteligencia Emocional', percentage_score: Math.min(Math.round(baseScore * 0.9 + 5), 100), maturity_level: getMaturityLevel(baseScore * 0.9), evidence_notes: `Basado en test ${testType}` },
      { pillar_name: 'Gestión del Tiempo',     percentage_score: Math.min(Math.round(baseScore * 0.85 + 8), 100), maturity_level: getMaturityLevel(baseScore * 0.85), evidence_notes: `Basado en test ${testType}` },
      { pillar_name: 'Liderazgo y Toma de Decisiones', percentage_score: Math.min(Math.round(baseScore * 0.8 + 10), 100), maturity_level: getMaturityLevel(baseScore * 0.8), evidence_notes: `Basado en test ${testType}` },
      { pillar_name: 'Adaptabilidad al Cambio', percentage_score: Math.min(Math.round(baseScore * 0.88 + 6), 100), maturity_level: getMaturityLevel(baseScore * 0.88), evidence_notes: `Basado en test ${testType}` },
    ];
  };

  // Determinar nivel de madurez según porcentaje
  const getMaturityLevel = (score) => {
    if (score >= 76) return 'Optimizado / Mentor';
    if (score >= 51) return 'Competente / Consistente';
    if (score >= 26) return 'En Desarrollo';
    return 'Inicial / Reactivo';
  };

  // Renderizar la interfaz de preguntas según el test activo
  const renderTestInterface = () => {
    const testId = selectedTests[0];
    const questions = getActiveQuestions();
    const question = questions[currentQuestionIndex];
    if (!question) return <p className="text-gray-500 text-center py-8">No hay preguntas disponibles</p>;

    // === TEST MBTI: 2 opciones por pregunta ===
    if (testId === 'mbti') {
      return (
        <div className="space-y-6">
          {/* Número de pregunta */}
          <h3 className="text-xl font-bold text-gray-900">Pregunta {currentQuestionIndex + 1} de {questions.length}</h3>
          {/* Texto de la pregunta */}
          <p className="text-gray-700 text-lg">{question.text}</p>
          {/* Opciones de respuesta */}
          <div className="space-y-3">
            {question.options.map((opt, idx) => (
              <button key={idx} onClick={() => handleMBTIAnswer(question.id, opt.value)}
                className={`w-full text-left p-4 border-2 rounded-lg transition-all duration-200 ${
                  mbtiAnswers[question.id] === opt.value ? 'border-indigo-500 bg-indigo-50 shadow-md' : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                }`}>
                {/* Indicador de selección */}
                <span className={`inline-block w-5 h-5 rounded-full border-2 mr-3 align-middle ${
                  mbtiAnswers[question.id] === opt.value ? 'bg-indigo-600 border-indigo-600' : 'border-gray-400'
                }`}></span>
                {opt.text}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // === TEST VIA: escala Likert de 5 puntos ===
    if (testId === 'via') {
      return (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900">Pregunta {currentQuestionIndex + 1} de {questions.length}</h3>
          {/* Mostrar virtud y fortaleza evaluada */}
          <div className="flex items-center space-x-2 text-sm">
            <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">{question.virtue}</span>
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-medium">{question.strength}</span>
          </div>
          <p className="text-gray-700 text-lg italic">"{question.text}"</p>
          {/* Opciones Likert */}
          <div className="space-y-2">
            {viaLikertOptions.map((opt) => (
              <button key={opt.value} onClick={() => handleVIAAnswer(question.id, opt.value)}
                className={`w-full text-left p-3 border-2 rounded-lg transition-all duration-200 ${
                  viaAnswers[question.id] === opt.value ? 'border-purple-500 bg-purple-50 shadow-md' : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                }`}>
                <span className={`inline-block w-5 h-5 rounded-full border-2 mr-3 align-middle ${
                  viaAnswers[question.id] === opt.value ? 'bg-purple-600 border-purple-600' : 'border-gray-400'
                }`}></span>
                {opt.text}
              </button>
            ))}
          </div>
        </div>
      );
    }

    // === TEST ENEAGRAMA: elección forzada entre 2 afirmaciones ===
    if (testId === 'enneagram') {
      return (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900">Pregunta {currentQuestionIndex + 1} de {questions.length}</h3>
          <p className="text-gray-600">Elige la afirmación que más te describe en general:</p>
          <div className="space-y-4">
            {/* Opción A */}
            <button onClick={() => handleEnneagramAnswer(question.id, 'A')}
              className={`w-full text-left p-5 border-2 rounded-lg transition-all duration-200 ${
                enneagramAnswers[question.id] === 'A' ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
              }`}>
              <span className={`inline-block w-5 h-5 rounded-full border-2 mr-3 align-middle ${
                enneagramAnswers[question.id] === 'A' ? 'bg-green-600 border-green-600' : 'border-gray-400'
              }`}></span>
              {question.optionA}
            </button>
            {/* Opción B */}
            <button onClick={() => handleEnneagramAnswer(question.id, 'B')}
              className={`w-full text-left p-5 border-2 rounded-lg transition-all duration-200 ${
                enneagramAnswers[question.id] === 'B' ? 'border-green-500 bg-green-50 shadow-md' : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
              }`}>
              <span className={`inline-block w-5 h-5 rounded-full border-2 mr-3 align-middle ${
                enneagramAnswers[question.id] === 'B' ? 'bg-green-600 border-green-600' : 'border-gray-400'
              }`}></span>
              {question.optionB}
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  // Verificar si se respondieron suficientes preguntas para finalizar
  const canProceed = () => {
    const questions = getActiveQuestions();
    const answers = getActiveAnswers();
    // Requiere responder TODAS las preguntas
    return Object.keys(answers).length >= questions.length;
  };

  // Retroceder a la pregunta anterior
  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(prev => prev - 1);
  };

  // Avanzar a la siguiente pregunta manualmente
  const goToNextQuestion = () => {
    const questions = getActiveQuestions();
    if (currentQuestionIndex < questions.length - 1) setCurrentQuestionIndex(prev => prev + 1);
  };

  // =================== PANTALLA DE SELECCIÓN DE TESTS (paso 0) ===================
  if (currentStep === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Selección de Tests</h1>
          <p className="text-gray-600">Elige los tests psicológicos que deseas realizar</p>
        </div>
        {/* Grid de tarjetas de tests disponibles */}
        <div className="grid md:grid-cols-3 gap-6">
          {tests.map((test) => {
            const Icon = test.icon;
            const isSelected = selectedTests.includes(test.id);
            return (
              <div key={test.id} onClick={() => handleTestSelection(test.id)}
                className={`bg-white rounded-lg p-6 card-shadow cursor-pointer transition-all ${
                  isSelected ? 'ring-2 ring-indigo-500 border-indigo-500' : 'border border-gray-200 hover:border-gray-300'
                }`}>
                <div className="flex items-start justify-between mb-4">
                  <Icon className="h-8 w-8 text-indigo-600" />
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300'
                  }`}>{isSelected && <CheckCircleIcon className="h-4 w-4 text-white" />}</div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{test.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{test.description}</p>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{test.questions} preguntas</span>
                  <span>{test.duration}</span>
                </div>
              </div>
            );
          })}
        </div>
        {/* Botón para iniciar */}
        <div className="mt-8 flex justify-center">
          <button onClick={() => { if (selectedTests.length > 0) { setCurrentQuestionIndex(0); setCurrentStep(1); } }}
            disabled={selectedTests.length === 0}
            className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            Comenzar Tests Seleccionados
          </button>
        </div>
      </div>
    );
  }

  // =================== PANTALLA DE REALIZACIÓN DEL TEST (paso 1) ===================
  const questions = getActiveQuestions();
  const answers = getActiveAnswers();
  const progressPercent = questions.length > 0 ? (Object.keys(answers).length / questions.length) * 100 : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="bg-white rounded-lg card-shadow p-8">
        {/* Encabezado con nombre del test y botón volver */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">
              {tests.find(t => t.id === selectedTests[0])?.name}
            </h1>
            <button onClick={() => { setCurrentStep(0); setCurrentQuestionIndex(0); }}
              className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeftIcon className="h-4 w-4 mr-1" /> Volver
            </button>
          </div>
          {/* Barra de progreso */}
          <div className="flex items-center space-x-3">
            <div className="flex-1 bg-gray-200 rounded-full h-3">
              <div className="bg-indigo-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${progressPercent}%` }}></div>
            </div>
            <span className="text-sm font-medium text-gray-600">{Math.round(progressPercent)}%</span>
          </div>
          {/* Contador de respuestas */}
          <p className="text-sm text-gray-500 mt-2">{Object.keys(answers).length} de {questions.length} respondidas</p>
        </div>

        {/* Área de la pregunta actual */}
        {renderTestInterface()}

        {/* Controles de navegación inferior */}
        <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
          {/* Botón anterior */}
          <button onClick={goToPreviousQuestion} disabled={currentQuestionIndex === 0}
            className="flex items-center text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> Anterior
          </button>
          <div className="flex space-x-3">
            {/* Botón cancelar */}
            <button onClick={() => { setCurrentStep(0); setCurrentQuestionIndex(0); }}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
              Cancelar
            </button>
            {/* Botón siguiente (si no es la última pregunta) */}
            {currentQuestionIndex < questions.length - 1 && (
              <button onClick={goToNextQuestion}
                className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors">
                Siguiente <ArrowRightIcon className="h-4 w-4 ml-1" />
              </button>
            )}
            {/* Botón finalizar (solo cuando se respondieron todas) */}
            <button onClick={submitTestResults} disabled={!canProceed() || isSubmitting}
              className="flex items-center bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {isSubmitting ? (
                <><svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>Guardando...</>
              ) : (<>Finalizar Test <ArrowRightIcon className="h-4 w-4 ml-1" /></>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tests;